import { mapActions, mapState } from 'vuex';
import { toIsoDate } from '../../../../util/shared/tmc-global';

/**
 * June Resale "penalty outdated" warning, shared by the Resale Property List and Day of Sale.
 *
 * The penalties and fees sitting on the resale records are only as current as the last
 * Recalculate run, and nothing on either screen hints when that was. The saved
 * `resaleConfig.resalePenaltyDate` IS the staleness marker: both screens write it back after a
 * successful recalculation, so a saved date older than today means the numbers on screen were
 * calculated against a date that has already passed.
 *
 * Deliberately built against the SAVED date rather than the page's `penaltyDateValue` field:
 *   - typing a new date into the field changes nothing about the calculations, so the warning
 *     has to stay up until the user actually recalculates, and
 *   - both screens share the one saved date, so recalculating on either clears the warning on
 *     both.
 */

export const PENALTY_OUTDATED_MESSAGE = 'Penalty outdated - Recalculation necessary';

/**
 * Today in the county's own timezone, as YYYY-MM-DD. Built from the LOCAL getters on purpose -
 * `toIsoDate(new Date())` reads UTC, which would roll over to tomorrow during the evening in
 * every US timezone and warn a day early.
 */
export const countyLocalIsoToday = (now = new Date()) => {
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Are the saved penalty calculations stale?
 *
 * Date-only comparison: a penalty date OF today is not stale, it goes stale tomorrow - so a
 * morning load on the penalty date itself does not warn. `toIsoDate` normalises both shapes the
 * Penalty Date control can emit (`M-D-YYYY` from the classic input, `YYYY-MM-DD` from the native
 * one) into a string that compares correctly.
 *
 * No saved date at all - a county that has never recalculated - counts as stale, which is the
 * case that most needs the nudge. So does a date we cannot parse, rather than silently
 * pretending the numbers are current.
 */
export const isResalePenaltyStale = (resalePenaltyDate, now = new Date()) => {
  if (!resalePenaltyDate) return true;
  const penaltyIsoDate = toIsoDate(resalePenaltyDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(penaltyIsoDate)) return true;
  return countyLocalIsoToday(now) > penaltyIsoDate;
};

export default {
  data() {
    return {
      // Until resaleConfig has actually come back we cannot tell "never recalculated" from
      // "not loaded yet", and warning on the second would flash on every single page load.
      isResalePenaltyConfigLoaded: false,
    };
  },
  computed: {
    ...mapState({
      penaltyStaleResaleSettingItem: (state) => state.SystemConfig.resaleSettingItem || {},
    }),
    isResalePenaltyOutdated() {
      if (!this.isResalePenaltyConfigLoaded) return false;
      return isResalePenaltyStale(this.penaltyStaleResaleSettingItem.resalePenaltyDate);
    },
    // Pulses the Recalculate button itself; see .penalty-recalc-stale in assets/style.css.
    resalePenaltyStaleBtnClass() {
      return { 'penalty-recalc-stale': this.isResalePenaltyOutdated };
    },
  },
  methods: {
    ...mapActions('SystemConfig', {
      loadResalePenaltyConfig: 'loadConfig',
    }),
    // Call wherever the screen loads its list - re-reading the saved date is what makes the
    // warning clear straight after a recalculation, with no page reload.
    async refreshResalePenaltyStaleCheck() {
      await this.loadResalePenaltyConfig({ requiredScope: 'resaleConfig' });
      this.isResalePenaltyConfigLoaded = true;
    },
  },
};
